import type { ChatColorTheme } from '@oneToOne/shared/components/chat/theme/ChatThemeContext'
import { Transition, TRANSITION_TIMING_FUNCTION } from '@oneToOne/shared/twind'
import { css } from 'twind/css'

interface ChatInputCssProps {
  theme: ChatColorTheme
  isMobileWeb: boolean
}

export const chatInputContainer = ({ theme }: { theme: ChatColorTheme }) =>
  css({
    gap: 'var(--spacing-8)',
    maxHeight: '86px',
    background:
      theme === 'light'
        ? '#fff'
        : theme === 'transparent'
          ? 'transparent'
          : 'var(--cool-gray-1000)',
    padding: 'var(--spacing-8) var(--spacing-24)',
  })

export const chatButton = ({ theme, isMobileWeb }: ChatInputCssProps) =>
  css({
    transition: `filter 0.4s ${TRANSITION_TIMING_FUNCTION}`,
    background:
      theme === 'light'
        ? 'var(--gray-4)'
        : theme === 'transparent'
          ? 'var(--white)'
          : 'var(--cool-gray-700)',
    '&:hover': {
      filter: 'brightness(0.98)',
    },
    height: `${isMobileWeb ? '36px' : '30px'} !important`,
    width: `${isMobileWeb ? '36px' : '30px'} !important`,
    flex: 'unset',
    aspectRatio: '1 / 1',
    '&:focus-visible': {
      outline: `1px solid ${
        theme === 'light' ? 'var(--gray-6)' : 'var(--cool-gray-400)'
      }`,
    },
  })

export const textAreaContainer = css({
  ...Transition,
  '*, *::before, *::after': {
    boxSizing: 'border-box',
  },
})

export const textAreaLabel = ({ theme, isMobileWeb }: ChatInputCssProps) =>
  css({
    maxHeight: '64px',
    '&::after, textarea': {
      color: theme === 'light' ? 'var(--gray-9)' : 'var(--cool-gray-200)',
      width: 'auto',
      minWidth: '10px',
      gridArea: '2 / 1',
      font: 'inherit',
      fontSize: isMobileWeb ? '16px !important' : '12px !important',
      padding: 0,
      margin: 0,
      resize: 'none',
      background: 'none',
      appearance: 'none',
      border: 'none',
    },
    'textarea, textarea::after': {
      overflowY: 'hidden',
    },
    'textarea::after': {
      /* globalStyles.ts sets font-size to 18px with !important,
        as a result the sizing logic is broken to know when to move
        to the next line, the only way around this is to set !important
        for the font size being used within this container, which is not 18px */
      fontSize: isMobileWeb ? '16px !important' : '12px !important',
    },
    '&::after': {
      content: `attr(data-value) ' '`,
      visibility: 'hidden',
      whiteSpace: 'pre-wrap',
    },
    outline: `solid 1px ${
      theme === 'light' ? 'var(--gray-4)' : 'var(--cool-gray-700)'
    }`,
    '&:focus-within': {
      outline: `solid 1px ${
        theme === 'light' ? 'var(--gray-6)' : 'var(--cool-gray-400)'
      }`,
      'textarea:focus': {
        outline: 'none',
      },
    },
  })

export const textSnippetList = (dropdownStyle) =>
  css({
    ...dropdownStyle,
    background: 'var(--white)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
  })

export const textSnippetListItem = css({
  padding: 'var(--spacing-8)',
  cursor: 'pointer',
  background: 'var(--white)',
  display: 'flex',

  '&:hover': {
    background: 'var(--gray-3)',
  },
})

export const textSnippetListItemShortcut = css({
  color: 'var(--blk2)',
})

export const textSnippetListItemPhrase = css({
  color: 'var(--blk2)',
  overflow: 'hidden',
  display: '-webkit-box',
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': 1,
})
